/**
 * Admin Incident RPC definitions
 *
 * Admin endpoints for structured incident tracking — requires admin role.
 * Incidents are linked to nodes (agent installations) and customers (onboardings).
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { IncidentSeverity, IncidentStatus } from "@fm/db/frontend";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import { DatabaseError, Forbidden, RecordNotFoundError, Unauthorized } from "../errors.ts";

// ============================================================================
// Shared Schemas
// ============================================================================

/**
 * Timeline entry schema for incident events.
 * @since 1.0.0
 */
export const TimelineEntrySchema = Schema.Struct({
  timestamp: Schema.String,
  type: Schema.String,
  body: Schema.String,
  authorId: Schema.String,
});
export type TimelineEntrySchema = Schema.Schema.Type<typeof TimelineEntrySchema>;

// ============================================================================
// Request Schemas
// ============================================================================

/**
 * Pagination + filter input for listing incidents
 * @since 1.0.0
 */
export const IncidentListInput = Schema.Struct({
  limit: Schema.optionalWith(Schema.Number, { default: () => 50 }),
  offset: Schema.optionalWith(Schema.Number, { default: () => 0 }),
  status: Schema.optional(IncidentStatus),
  customerId: Schema.optional(Schema.String),
  nodeId: Schema.optional(Schema.String),
  severity: Schema.optional(IncidentSeverity),
});

/**
 * Input for finding a single incident by ID
 * @since 1.0.0
 */
export const IncidentFindByIdInput = Schema.Struct({
  id: Schema.String,
});

/**
 * Input for creating a new incident
 * @since 1.0.0
 */
export const IncidentCreateInput = Schema.Struct({
  nodeId: Schema.String,
  customerId: Schema.String,
  title: Schema.String,
  severity: IncidentSeverity,
});

/**
 * Input for adding a note to an incident timeline
 * @since 1.0.0
 */
export const IncidentAddNoteInput = Schema.Struct({
  id: Schema.String,
  body: Schema.String,
  authorId: Schema.String,
});

/**
 * Input for resolving an incident
 * @since 1.0.0
 */
export const IncidentResolveInput = Schema.Struct({
  id: Schema.String,
});

/**
 * Input for reopening an incident
 * @since 1.0.0
 */
export const IncidentReopenInput = Schema.Struct({
  id: Schema.String,
});

// ============================================================================
// Response Schemas
// ============================================================================

/**
 * Incident summary for list view.
 * @since 1.0.0
 */
export const IncidentSummary = Schema.Struct({
  id: Schema.String,
  nodeId: Schema.String,
  customerId: Schema.String,
  title: Schema.String,
  status: IncidentStatus,
  severity: IncidentSeverity,
  createdAt: Schema.String,
  resolvedAt: Schema.NullOr(Schema.String),
});
export type IncidentSummary = Schema.Schema.Type<typeof IncidentSummary>;

/**
 * Paginated response for incidents
 * @since 1.0.0
 */
export const IncidentListResponse = Schema.Struct({
  items: Schema.Array(IncidentSummary),
  total: Schema.Number,
});

/**
 * Incident detail — includes full timeline.
 * @since 1.0.0
 */
export const IncidentDetail = Schema.Struct({
  id: Schema.String,
  nodeId: Schema.String,
  customerId: Schema.String,
  title: Schema.String,
  status: IncidentStatus,
  severity: IncidentSeverity,
  timeline: Schema.Array(TimelineEntrySchema),
  createdAt: Schema.String,
  resolvedAt: Schema.NullOr(Schema.String),
});
export type IncidentDetail = Schema.Schema.Type<typeof IncidentDetail>;

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Admin Incident RPC group
 *
 * Provides incident CRUD and lifecycle management endpoints.
 * All endpoints require admin role via AdminMiddleware.
 *
 * @since 1.0.0
 */
export class AdminIncidentRpcs extends RpcGroup.make(
  Rpc.make("admin.incidents.findMany", {
    payload: IncidentListInput,
    success: IncidentListResponse,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.incidents.findById", {
    payload: IncidentFindByIdInput,
    success: IncidentDetail,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.incidents.create", {
    payload: IncidentCreateInput,
    success: IncidentDetail,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.incidents.addNote", {
    payload: IncidentAddNoteInput,
    success: IncidentDetail,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.incidents.resolve", {
    payload: IncidentResolveInput,
    success: IncidentDetail,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.incidents.reopen", {
    payload: IncidentReopenInput,
    success: IncidentDetail,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
) {}
